import { AfterViewInit, Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MasterStoreKey } from '@conf';
import {
  AswCommonStoreService,
  AswContextStoreService,
  ErrorsHandlerService,
  PageLoadingService,
  SystemDateService,
} from '@lib/services';
import { Observable, Subscription } from 'rxjs';
import { SampleSubHeaderComponent } from './sub-header/sample-sub-header/sample-sub-header.component';
import { environment } from '@env/environment';
import { DOCUMENT } from '@angular/common';
import { CheckinFunctionIdType, CheckinPageIdType } from '@common/interface';
import { CheckinSearchSubHeaderComponent as CheckinSearchSubHeaderComponent } from './sub-header/checkin-search-sub-header/checkin-search-sub-header.component';
import { CheckinMybookingSubHeaderComponent } from './sub-header/checkin-mybooking-sub-header/checkin-mybooking-sub-header.component';
import { CheckinSelectSubHeaderComponent } from './sub-header/checkin-select-sub-header/checkin-select-sub-header.component';
import { CheckinCompletedSubHeaderComponent } from './sub-header/checkin-completed-sub-header/checkin-completed-sub-header.component';
import { CheckinPassengerInputSubHeaderComponent } from './sub-header/checkin-passenger-input-sub-header/checkin-passenger-input-sub-header.component';
import { CheckinReviewSubHeaderComponent } from './sub-header/checkin-review-sub-header/checkin-review-sub-header.component';
import { CheckinSeatmapSubHeaderComponent } from './sub-header/checkin-seatmap-sub-header/checkin-seatmap-sub-header.component';
import { AswMasterService } from '@lib/services';
import { LOAD_MASTER_LIST } from '@conf';
import { lastValueFrom } from 'rxjs';
import { MOffice } from '@lib/interfaces';

@Component({
  selector: 'asw-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * ローディング画面表示制御 - 表示モード
   */
  public isPromotion$!: Observable<boolean>;
  /**
   * ローディング画面表示制御 - プロモーションモード判定フラグ
   */
  public isLoading$!: Observable<boolean>;

  constructor(
    private _aswCommonStoreService: AswCommonStoreService,
    private _aswContextStoreSvc: AswContextStoreService,
    private _errorHandlerSvc: ErrorsHandlerService,
    private _systemDateSvc: SystemDateService,
    private _loadingSvc: PageLoadingService,
    private _aswMasterService: AswMasterService,
    @Inject(NgZone) private _ngZone: NgZone,
    @Inject(DOCUMENT) private _document: Document
  ) {
    // ローディング画面の表示モードがプロモーションモードかの判定結果を取得
    this.isPromotion$ = this._loadingSvc.getIsPromotionMode$();
    // ローディング画面のローディング状態取得
    this.isLoading$ = this._loadingSvc.getPageLoadingState$();
  }

  // 言語（変更前の比較用）
  private lang = '';

  ngOnInit(): void {
    // エラーハンドリングServiceの初期化
    this._errorHandlerSvc.init();

    // システム日付Serviceの初期化
    this._systemDateSvc.init();

    //サブヘッダーを指定
    this.subPageSubscription = this._aswCommonStoreService.getPagefuncId$('page').subscribe((data) => {
      // NOTE: 各業務画面で必要なサブヘッダーを追加
      const funcPageId = (data.functionId ? data.functionId : '') + '-' + (data.pageId ? data.pageId : '');
      switch (funcPageId) {
        case CheckinFunctionIdType.CHECKIN + '-' + CheckinPageIdType.CHECKIN_SEARCH:
          this.subHeader = CheckinSearchSubHeaderComponent;
          break;
        case CheckinFunctionIdType.BOARDING_PROCEDURES + '-' + CheckinPageIdType.CHECKIN_MYBOOKING:
          this.subHeader = CheckinMybookingSubHeaderComponent;
          break;
        case CheckinFunctionIdType.CHECKIN + '-' + CheckinPageIdType.CHECKIN_SELECT:
          this.subHeader = CheckinSelectSubHeaderComponent;
          break;
        case CheckinFunctionIdType.CHECKIN + '-' + CheckinPageIdType.CHECKIN_PASSENGER_INPUT:
          this.subHeader = CheckinPassengerInputSubHeaderComponent;
          break;
        case CheckinFunctionIdType.CHECKIN + '-' + CheckinPageIdType.CHECKIN_REVIEW:
          this.subHeader = CheckinReviewSubHeaderComponent;
          break;
        case CheckinFunctionIdType.CHECKIN + '-' + CheckinPageIdType.CHECKIN_COMPLETED:
          this.subHeader = CheckinCompletedSubHeaderComponent;
          break;
        case CheckinFunctionIdType.CHECKIN + '-' + CheckinPageIdType.CHECKIN_SEATMAP:
          this.subHeader = CheckinSeatmapSubHeaderComponent;
          break;
        default:
          this.subHeader = SampleSubHeaderComponent;
          break;
      }
    });

    // 言語が変更された際にキャッシュをロードする。
    this._aswContextStoreSvc.getAswContext$().subscribe((data) => {
      if (data.lang && this.lang && data.lang !== this.lang) {
        lastValueFrom(this._aswMasterService.load(LOAD_MASTER_LIST));
      }
      this.lang = data.lang;
    });
  }

  ngOnDestroy(): void {
    if (this.subPageSubscription) {
      this.subPageSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this._ngZone.runOutsideAngular(() => {
      this._setDynatraceTag();
      this._setEnsightenTag();
    });
  }

  /**
   * Dynatrace用scriptタグを設置
   */
  private _setDynatraceTag() {
    let key: 'res' | 'srv' | 'exc' | 'cki';
    const URL = window.location.href;
    switch (true) {
      case URL.includes(`${environment.spa.baseUrl}${environment.spa.app.res}`):
        key = 'res';
        break;
      case URL.includes(`${environment.spa.baseUrl}${environment.spa.app.srv}`):
        key = 'srv';
        break;
      case URL.includes(`${environment.spa.baseUrl}${environment.spa.app.exc}`):
        key = 'exc';
        break;
      case URL.includes(`${environment.spa.baseUrl}${environment.spa.app.cki}`):
        key = 'cki';
        break;
      default:
        return;
    }

    if (environment.dynatrace[key].disable || !environment.dynatrace[key].url) {
      return;
    }

    const $script: HTMLScriptElement = this._document.createElement('script');
    $script.async = true;
    $script.type = 'text/javascript';
    $script.src = environment.dynatrace[key].url;
    $script.crossOrigin = 'anonymous';
    const $head: HTMLHeadElement = this._document.getElementsByTagName('head')[0];
    if ($head) {
      $head.appendChild($script);
    }
  }

  /**
   * Ensighten用scriptタグを設置
   */
  private _setEnsightenTag() {
    // 操作オフィスコード取得
    const operationOfficeCode = this._aswContextStoreSvc.aswContextData.pointOfSaleId;
    // オフィスマスタ情報
    const offices: MOffice[] = this._aswMasterService.aswMaster[MasterStoreKey.OFFICE_ALL] ?? [];
    // Ensightenタグ利用可否フラグ
    let isAvailableEnsightenTag: boolean | undefined;
    if (Array.isArray(offices) && offices.length > 0) {
      isAvailableEnsightenTag = offices.find(
        (office) => office.office_code === operationOfficeCode
      )?.available_ensighten_tag;
    }
    if (!isAvailableEnsightenTag) {
      return;
    }

    const $script: HTMLScriptElement = this._document.createElement('script');
    $script.id = 'EnsightenScript';
    $script.type = 'text/javascript';
    $script.src = environment.ensighten.url;
    const $head: HTMLHeadElement = this._document.getElementsByTagName('head')[0];
    if ($head) {
      $head.appendChild($script);
    }
  }

  subPageSubscription?: Subscription;
  public subHeader: any;
}
